import { CloseIcon } from 'assets/icons/CloseIcon'
import { WarningTriangleIcon } from 'assets/icons/WarningTriangleIcon'
import { Modal } from 'components/Modal/Modal'
import { clearSinglePayroll, getSinglePayroll } from 'features/payrolls/payrollsSlice'
import useModalClose from 'hooks/useModalClose'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { ColoredDot, darkTheme, Flex, lightTheme, ProfilePicture, StyledContainer, ThinSeparator, WarningContainer } from 'styling/GlobalStyles'
import { BodyText, Head1, Head2, Head4, Head5, OtherText } from 'styling/Typography'
import { } from 'types/api/payrollsApi.interface'
import { formatDateForDisplay } from 'utils/helpers'
import useFocusFirstInput from 'utils/useFocusFirstInput'
import Avatar from '../../assets/images/profileAvatar.png'
import { attendanceTypesOptions } from 'pages/Attendance/data'

export const ViewPayrollModal = ({ closeModal, id }: { closeModal: any, id: number }) => {
    useFocusFirstInput()
    useModalClose(closeModal)
    const dispatch = useDispatch<AppDispatch>()
    const { singlePayroll } = useSelector((state: RootState) => state.payrolls)
    const { darkMode } = useSelector((store: RootState) => store.globals)


    useEffect(() => {
        dispatch(getSinglePayroll(id))

        return (() => {
            dispatch(clearSinglePayroll())
        })
    }, [])


    const AttendanceItem = ({ a, count }: { a: number, count: number }) => {

        const attendance = attendanceTypesOptions.find(at => at.value == a) ?? { color: 'vacationColor', label: 'Unknown' }
        const color = darkMode ? darkTheme[attendance.color] : lightTheme[attendance.color]
        return <Flex column gap='10px'>
            <Flex width='100%' between >
                <Flex center gap='1rem' >
                    <ColoredDot color={color} /><BodyText>{attendance.label}</BodyText>
                </Flex>
                <Head5>{count}</Head5>
            </Flex>
            <ThinSeparator margin={'0'} />
        </Flex>
    }

    const TextItem = ({ title, amount, orangeAmount }: { title: string, amount: any, orangeAmount?: boolean }) => {
        return <Flex width='100%' between center>
            <BodyText>{title}</BodyText>
            <Head5 color={orangeAmount ? '#ff9900' : null} >{amount}</Head5>
        </Flex>
    }

    const format = (input = 0): string => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(input);
    }


    return (
        <Modal black width='90vw' noMaxH >
            <Flex width='100%' column gap='1rem' >

                <Flex width='100%' between center gap='1rem' >
                    <Flex gap='1rem' >
                        <ProfilePicture style={{ height: '64px', width: '64px' }} >
                            <img
                                src={singlePayroll?.user.profileImageUrl || Avatar}
                                style={{ height: '64px', width: '64px' }}
                                alt=''
                                onError={({ currentTarget }) => {
                                    currentTarget.onerror = null
                                    currentTarget.src = Avatar
                                }}
                            />
                        </ProfilePicture>
                        <Flex column gap='2px'>
                            <OtherText>{singlePayroll?.user.accessCardId}</OtherText>
                            <Head2>{(singlePayroll?.user.firstName || '/') + ' ' + (singlePayroll?.user.lastName || '/')}</Head2>
                            <BodyText>{singlePayroll?.user?.role?.name || '/'}</BodyText >
                        </Flex>
                    </Flex>
                    {singlePayroll?.generationErrorMessage && (
                        <WarningContainer style={{ width: '40%' }}>
                            {WarningTriangleIcon}
                            <BodyText>{singlePayroll.generationErrorMessage}</BodyText>
                        </WarningContainer>
                    )}
                    <Flex center gap='1rem' >
                        <BodyText>{formatDateForDisplay(singlePayroll?.startDate)} - {formatDateForDisplay(singlePayroll?.endDate)}</BodyText>
                        <CloseIcon onClick={closeModal} />
                    </Flex>

                </Flex>

                <Flex width='100%' between gap='1rem' >
                    <StyledContainer width='60%' height='auto'>
                        <Flex column gap='1rem' >
                            <Head4 color='#ff9900'>ATTENDANCE</Head4>
                            <Flex column gap='10px'>
                                <AttendanceItem a={1} count={singlePayroll?.attendance.present || 0} />
                                <AttendanceItem a={2} count={singlePayroll?.attendance.absent || 0} />
                                <AttendanceItem a={3} count={singlePayroll?.attendance.leave || 0} />
                                <AttendanceItem a={4} count={singlePayroll?.attendance.excused || 0} />
                                <AttendanceItem a={5} count={singlePayroll?.attendance.holidayOut || 0} />
                                <AttendanceItem a={6} count={singlePayroll?.attendance.sickLeave || 0} />
                                <AttendanceItem a={7} count={singlePayroll?.attendance.offDay || 0} />
                                <AttendanceItem a={8} count={singlePayroll?.attendance.training || 0} />
                                <AttendanceItem a={9} count={singlePayroll?.attendance.vacation || 0} />
                                <AttendanceItem a={10} count={singlePayroll?.attendance.maternityLeave || 0} />
                                <AttendanceItem a={11} count={singlePayroll?.attendance.holidayIn || 0} />
                                <AttendanceItem a={12} count={singlePayroll?.attendance.unknown || 0} />
                            </Flex>
                            <Flex column >
                                <Head5>Total Working Days</Head5>
                                <Head2 color='#ff9900' >{singlePayroll?.attendance.workingDays || 0}/{singlePayroll?.attendance.totalDays || 0}</Head2>
                            </Flex>
                        </Flex>
                    </StyledContainer>



                    <Flex width='80%' column between gap='1rem' >
                        <StyledContainer width='100%' height='auto'>
                            <Flex column gap='1rem' >
                                <Head4 color='#ff9900'>EARNINGS</Head4>
                                <Flex column >
                                    <TextItem title='Basic Salary' amount={format(singlePayroll?.basicSalaryAmount || 0)} />
                                    <ThinSeparator margin='10px 0' />
                                    <TextItem title='Extra duty allowance' amount={format(singlePayroll?.extraDutyAllowanceAmount || 0)} />
                                    <ThinSeparator margin='10px 0' />
                                    <TextItem title='Respon. allowance' amount={format(singlePayroll?.responsibilityAllowanceAmount || 0)} />
                                    <ThinSeparator margin='10px 0' />
                                    <TextItem title='General allowance' amount={format(singlePayroll?.generalAllowanceAmount || 0)} />
                                    <ThinSeparator margin='10px 0' />
                                    <TextItem title='Total cash allowances' orangeAmount amount={format(singlePayroll?.totalCashAllowanceAmount || 0)} />
                                </Flex>
                            </Flex>
                        </StyledContainer>

                        <StyledContainer width='100%' height='auto'>
                            <Flex column gap='1rem' >
                                <Head4 color='#ff9900'>TAXABLE / CHARGEABLE INCOME</Head4>
                                <Flex column >
                                    <TextItem title='Chargable income' amount={format(singlePayroll?.chargeableIncomeAmount || 0)} />
                                    <ThinSeparator margin='10px 0' />
                                    <TextItem title='Chargable emoulment' amount={format(singlePayroll?.chargebleEmolumentAmount || 0)} />
                                </Flex>
                            </Flex>
                        </StyledContainer>

                        <StyledContainer width='100%' height='auto'>
                            <Flex column gap='1rem' >
                                <Head4 color='#ff9900'>DEDUCTIONS & CONTRIBUTIONS</Head4>
                                <Flex column >
                                    <TextItem title='SSNIT' amount={format(singlePayroll?.ssnitAmount)} />
                                    <ThinSeparator margin='10px 0' />
                                    <TextItem title='Tax deduction' amount={format(singlePayroll?.taxDeductionAmount)} />
                                    <ThinSeparator margin='10px 0' />
                                    <TextItem title='Total deduction' amount={format(singlePayroll?.totalDeductionAmount)} />
                                    <ThinSeparator margin='10px 0' />
                                    <TextItem title='Net salary' orangeAmount amount={format(singlePayroll?.netSalarySumAmount)} />
                                </Flex>
                            </Flex>
                        </StyledContainer>
                    </Flex>

                    <Flex width='100%' between gap='1rem' column>
                        <StyledContainer width='100%' height='auto'>
                            <Flex column gap='1rem' >
                                <Head4 color='#ff9900'>OVERTIME & EXTRAS</Head4>
                                <Flex column >
                                    <TextItem title='Overtime' amount={format(singlePayroll?.overtimeAmount)} />
                                    <ThinSeparator margin='10px 0' />
                                    <TextItem title='6th Day' amount={format(singlePayroll?.sixthDayAmount)} />
                                    <ThinSeparator margin='10px 0' />
                                    <TextItem title='Holiday' amount={format(singlePayroll?.holidayAmount)} />
                                    <ThinSeparator margin='10px 0' />
                                    <TextItem title='Total overtime' amount={format(singlePayroll?.totalOvertimeAmount)} />
                                    <ThinSeparator margin='10px 0' />
                                    <TextItem title='Total cash emolument' amount={format(singlePayroll?.totalCashEmolumentSumAmount)} />
                                    <ThinSeparator margin='10px 0' />
                                    <TextItem title='Chargable emolument' orangeAmount amount={format(singlePayroll?.chargebleEmolumentAmount)} />
                                    <ThinSeparator margin='10px 0' />
                                    <TextItem title='Tax on overtime' orangeAmount amount={format(singlePayroll?.taxOnOvertimeAmount)} />
                                    <ThinSeparator margin='10px 0' />
                                    <TextItem title='Total deduction' orangeAmount amount={format(singlePayroll?.totalDeductionAmount)} />
                                </Flex>
                            </Flex>
                        </StyledContainer>

                        <StyledContainer width='100%' height='auto'>
                            <Flex column gap='1rem' >
                                <Flex width='100%' between >
                                    <Head4>NET SALARY FINAL</Head4>
                                    <Flex>
                                        <Head1 color='#ff9900'> <span style={{ fontSize: 12 }} > GH₵</span> {format(singlePayroll?.netSalaryAmount) || 0}</Head1>
                                    </Flex>
                                </Flex>
                                <Flex width='100%' between >
                                    <Head4>SSNIT</Head4>
                                    <Flex>
                                        <Head1 color='#ff9900'> <span style={{ fontSize: 12 }} > GH₵</span> {format(singlePayroll?.ssnitAmount) || 0}</Head1>
                                    </Flex>
                                </Flex>
                                <Flex width='100%' between >
                                    <Head4>TOTAL COST</Head4>
                                    <Flex>
                                        <Head1 color='#ff9900' > <span style={{ fontSize: 12 }} > GH₵</span> {format(singlePayroll?.totalCostAmount) || 0}</Head1>
                                    </Flex>
                                </Flex>
                            </Flex>
                        </StyledContainer>
                    </Flex>


                </Flex>
            </Flex>

        </Modal >
    )
}